import type { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { AwaitingVoltageChip } from '~/components/balance/AwaitingVoltageMark'
import { HelpIcon } from '~/components/common/HelpIcon'
import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { isAwaitingVoltage } from '~/models/awaitingVoltage'
import { KOMA_DIVIDER_BORDER, isKomaDividerAfter } from '~/models/komaDivider'
import { formatKwh } from '~/models/kwhDisplay'

// `pages/BalanceDemandDetail` / `pages/BalanceGenerationDetail` の両方から再利用される
// 48 コマ (時刻別) の計画/実績テーブル。 ドメイン固有のフィールド名 (demandPlan/genPlan 等) は
// 呼び出し側で本 row 型にマッピングしてから渡す。

export type BalanceKomaRow = {
  koma: number
  time: string
  /**
   * 計画値。`null` = このコマに対応する計画が存在しない (= 「―」表示)。
   * 需要側で電圧階級を絞ると発生する (確定需要想定は BG 全体単位、#2001)。
   */
  plan: number | null
  actual: number | null
  /**
   * 実績セルの強調表示状態。 いずれも地・文字色は design の token に対応する
   * (下の td の実装コメントに design の該当行を記載)。
   * - `'missing'`: 実績欠損 (= 「×」 表示 + **warning** の地 + negativeMid の太字)。
   *   全損 (negative) より 1 段軽い深刻度。
   * - `'interpolated'`: 補完値 (= 中立 tertiary の地 + midEmphasis の italic)。
   *   「値はあるが推定」で欠損とは軸が違うため有彩色は使わない。
   * - `undefined`: 通常実績 / 未来コマ (= 実績なしは「―」)
   *
   * BalanceGenerationDetail 側は欠損/補間の概念を持たないため常に `undefined`。
   */
  actualStatus?: 'missing' | 'interpolated'
  /**
   * 速報が未着の電圧階級 (空・未指定 = 確定)。 `actualStatus` とは別軸で、
   * 補完済み × 低圧未着は補完の見た目のまま時刻のセルにチップも付く。 発電詳細は概念を持たないので渡さない。
   */
  awaitingVoltageClasses?: readonly VoltageClass[]
  /**
   * backend が算出済みの誤差率(%)。指定された場合はこの値をそのまま表示し、
   * KomaTable 側では再計算しない。
   * - `number`: backend 算出値 (発電は (実績−計画)/計画×100、計画0コマは 0)
   * - `null`: 計画/実績が欠測で誤差率が定義できない (「―」表示)
   * - `undefined`: caller が backend 値を渡さない (= 需要側。plan/actual から従来式で算出)
   *
   * 需要と発電で誤差率の符号定義が逆 (doji-bg.md / doji-hatsu.md) なため、backend
   * 値を渡せる発電側はこの field 経由で正しい符号・色を表示する。
   */
  errorPct?: number | null
}

type Props = {
  data: BalanceKomaRow[]
  /**
   * 現在コマ (= 当日を表示している時だけ意味を持つ)。 **0 は「該当なし」**
   * (backend の option.None → 0。 [[DemandTooltip]] の `currentKoma > 0` と同じ規約)
   * なので、 過去日・未来日ではハイライトを出さない。
   *
   * spec は需要・発電の**両方**が同じ要求を持つ:
   * `doji-hatsu.md:264-266` AC-BDH-022「WHILE 当日のデータを表示中 /
   * 数値テーブルの現在コマの行をハイライト表示する」、
   * `doji-bg.md:382` にも同文がある。 共有部品側に持たせて 2 画面で揃える。
   */
  currentKoma?: number
  /**
   * 列見出し ({@link labelKeyPrefix}.time / .plan / .actual / .error) と
   * 欠損ラベル ({@link labelKeyPrefix}.missing) の i18n key prefix。
   * 例: `'page:balanceDemandDetail.table'` / `'page:balanceGenerationDetail.table'`
   */
  labelKeyPrefix: string
  /**
   * 誤差(%) の色分け規則。 **呼び出し側が明示する** (`errorPct` の有無で暗黙に
   * 切り替えない)。
   *
   * - `'threshold'` (既定) … 乖離が {@link DEVIATION_THRESHOLD_PCT} 未満なら
   *   色を付けず値だけ出す。 需要詳細の規則で、 design `js/bg-region-data.js`
   *   L102-107 が根拠。 48 行が常時カラフルだと「どのコマを見に行くべきか」が
   *   読めなくなるため。
   * - `'always'` … 0 でない誤差は必ず正負で色を分ける。 **発電詳細はこちら**。
   *   spec `docs/specs/balance/doji-hatsu.md` の AC-BDH-025 が Ubiquitous で
   *   「誤差列の色分けをプラス（計画超過）とマイナス（計画未達）で視覚的に区別して
   *   表示する」 と定めており、 閾値の但し書きが無い。 需要側の閾値をそのまま
   *   当てると 1.5% 未満の超過/未達が同じ中立色になり AC に反する
   *   (2026-09-03 レビュー指摘)。
   */
  errorColorRule?: 'threshold' | 'always'
  /**
   * 左ペインを **既定では横スクロールさせず**、 最終列 (誤差) に右余白を足すか。
   * 列見出しは折り返しを許して 4 列を収め、 それでも収まらない環境 (Windows の
   * Meiryo は字幅が広い) では横スクロールで最終列へ届くようにする (#9509)。
   *
   * design はこの対策を `.region-left` (= **需要詳細の 380px ペイン専用**) に
   * スコープしている (`css/balance.css:300,303-304`)。 発電詳細の
   * `.single-bg-left` は `overflow-y:auto` だけで、 `overflow-x` も最終列の
   * padding も持たない (同 :424)。
   *
   * 共有コンポーネント側で常に当てると、 **280px のままの発電詳細で最終列
   * (誤差) が切り取られて到達不能になる** (2026-09-04 レビュー指摘)。 幅を
   * 決めるのは呼び出し側なので、 この対策の要否も呼び出し側が明示する。
   */
  fitsWithoutHorizontalScroll?: boolean
  /**
   * 列見出しに添える補足 (`?` アイコン + tooltip)。 **列ごとに opt-in**。
   *
   * design は誤差(%) の説明を**需要詳細にだけ**置いている
   * (`balance/bg-region.html:158` の `data-tooltip`)。 発電側の 2 画面
   * (`balance/hatsu.html:158` / `balance/hatsu-detail.html:90`) は素の
   * `<th>誤差 (%)</th>` で補足を持たない。 共有部品側で無条件に出すと
   * design に無い補足が発電側へ増えるので、 呼び出し側が渡した列にだけ出す。
   */
  headerHints?: Partial<Record<ColumnKey, KomaTableHeaderHint>>
}

/**
 * 誤差(%) に色を付ける乖離の閾値。 これ未満は値だけ出して強調を外す。
 *
 * design `js/bg-region-data.js` L102-107 (2026-08-14) が根拠。 旧実装は 0.1% でも
 * 全て赤/緑に塗っており、 48 行が常時カラフルで「どのコマを見に行くべきか」が
 * 読めなくなっていた。 小さい乖離も**値は出す** — 隠すのではなく強調を外すだけ。
 * 同じ 1.5% を発電側の card 警告 (`js/hatsu-data.js` DEV_THRESHOLD) も使う。
 *
 * **export しているのは、 呼び出し側が見出しの説明文へ差し込むため。** 文言に
 * 1.5 を直書きすると、 閾値を変えた時に説明だけが古いまま残る (しかも ja / en の
 * 2 言語ぶん)。
 *
 * 判定は **`>=` (閾値ちょうどを含む)**。 design `js/bg-region-data.js` の
 * `Math.abs(pct) >= DEV_THRESHOLD` と同じ。 説明文も「以上」 と書くこと —
 * design 側の文言は「超えた」 だが、 それだと 1.5% ちょうどのコマが**色は付くのに
 * 説明では対象外**になる (2026-09-07 レビュー指摘)。 プロト側の文言も併せて是正した。
 */
export const DEVIATION_THRESHOLD_PCT = 1.5

/**
 * 最終列 (誤差) の右余白。 design `css/balance.css:303-304` の
 * `.region-left .ecl-table th:last-child, td:last-child { padding-right: var(--space-5) }`
 * = **20px** (`css/tokens.css:212`)。
 *
 * overlay スクロールバー (macOS) は幅 0 で `scrollbar-gutter` が効かないため、
 * 右寄せの数値がバーに隠れないよう最終列だけ余白を足す。
 */
const LAST_COLUMN_PADDING_RIGHT = '20px'

/**
 * 現在コマ行の輪郭を `td` ごとの inset shadow で描く。
 *
 * **地色でなく輪郭にする** のは 2026-09-02 のデザイナー判断 (issue #6872 の 5 番、
 * PR #7013)。 この表は既に偶数行の zebra・欠損の warning 地・補完の tertiary 地を
 * 地色で使っており、 現在コマにも地色を当てると**どれが状態でどれが位置かが
 * 読めなくなる**。 需要想定詳細の [[KomaGrid]] も同じ理由で
 * `inset 0 0 0 1px Border.accentPrimary` を使っている。
 *
 * `tr` に box-shadow を当てないのは、 この表が `border-collapse: collapse` で
 * 描かれており行要素の shadow がブラウザによって描かれないため。 セルごとに
 * 上下の線を引き、 両端のセルだけ左右の線を足して 1 本の枠に見せる。
 */
const currentKomaOutline = (position: 'first' | 'middle' | 'last'): string => {
  const color = LightModeSemanticColors.Border.accentPrimary.value
  const edges = [`inset 0 1px 0 0 ${color}`, `inset 0 -1px 0 0 ${color}`]
  if (position === 'first') edges.push(`inset 1px 0 0 0 ${color}`)
  if (position === 'last') edges.push(`inset -1px 0 0 0 ${color}`)
  return edges.join(', ')
}

/**
 * 小数 1 桁に丸めて 0.0 になる乖離の境界。
 *
 * これ未満を素通しすると `▲0.0%` という読めない表記が出る
 * (design `js/bg-region-data.js` L109 と同じ扱い)。
 */
const ZERO_DISPLAY_PCT = 0.05

const COLUMNS = [
  { key: 'time', align: 'left' as const },
  { key: 'plan', align: 'right' as const },
  { key: 'actual', align: 'right' as const },
  { key: 'error', align: 'right' as const },
]

/** 列見出しの key (`labelKeyPrefix` の suffix と同じ)。 */
type ColumnKey = (typeof COLUMNS)[number]['key']

/** 見出しに添える補足 (`?` アイコン + hover / focus で出る説明)。 */
export type KomaTableHeaderHint = {
  /** 説明本文。 */
  content: string
  /** 見出し名を含む読み上げラベル (design `bg.html` の `.head-info` aria-label 相当)。 */
  ariaLabel: string
}

/**
 * 48 コマの計画値/実績値/誤差率テーブル (= design HTML の左ペイン固定テーブル)。
 * BalanceDemandDetail / BalanceGenerationDetail で行 shape が違う (実績欠損概念の有無) ため
 * 呼び出し側で {@link BalanceKomaRow} にマッピングして渡す。
 */
export const KomaTable = ({
  data,
  currentKoma,
  labelKeyPrefix,
  errorColorRule = 'threshold',
  fitsWithoutHorizontalScroll = false,
  headerHints,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const formatError = (
    row: BalanceKomaRow,
    isAwaiting: boolean,
  ): {
    text: string
    color: string
    isEmphasized: boolean
    /** 乖離を強調する対象か (閾値以上、 または `always` で 0 以外)。 */
    isOverThreshold: boolean
  } | null => {
    // backend 算出値 (errorPct) が渡された場合はそれを SoT として表示する
    // (発電は符号が需要と逆)。
    // 渡されない場合 (= 需要側) のみ plan/actual から従来式で再計算する。
    let pct: number
    if (row.errorPct !== undefined) {
      if (row.errorPct === null) return null
      pct = row.errorPct
    } else {
      if (row.actual === null || row.plan === null) return null
      // 計画値 0 のコマは 0 除算だけ避けて 0.0% を出す。 spec
      // docs/specs/balance/doji-bg.md L313「計画値が0の場合は 0.0% と表示する」。
      // 旧実装は plan === 0 を弾いて「—」にしており、 計画 0 と欠測が同じ見た目に
      // なっていた (design js/bg-region-data.js L96-100 も同じ経緯で是正済み)。
      pct = row.plan === 0 ? 0 : ((row.plan - row.actual) / row.plan) * 100
    }

    const neutralColor = LightModeSemanticColors.Text.highEmphasis.value
    if (Math.abs(pct) < ZERO_DISPLAY_PCT) {
      return {
        text: '0.0%',
        color: neutralColor,
        isEmphasized: false,
        isOverThreshold: false,
      }
    }

    const text =
      pct < 0 ? `▲${Math.abs(pct).toFixed(1)}%` : `${pct.toFixed(1)}%`
    // 低圧未着のコマは低圧ぶんが欠けた実績から出した誤差なので、 乖離の強調を
    // 付けない (付けると「計画より大きく下振れ」 と誤読する。 design 辞書
    // `screens/balance-demand-detail.md` の「低圧未着」 行)。 値は隠さない。
    if (isAwaiting) {
      return {
        text,
        color: LightModeSemanticColors.Text.midEmphasis.value,
        isEmphasized: false,
        isOverThreshold: false,
      }
    }

    // `always` は 0 判定 (上の ZERO_DISPLAY_PCT) を抜けた時点で必ず色を付ける。
    const isOverThreshold =
      errorColorRule === 'always' || Math.abs(pct) >= DEVIATION_THRESHOLD_PCT
    if (pct < 0) {
      return {
        text,
        color: isOverThreshold
          ? LightModeSemanticColors.Text.negativeMid.value
          : neutralColor,
        isEmphasized: isOverThreshold,
        isOverThreshold,
      }
    }
    return {
      text,
      color: isOverThreshold
        ? LightModeSemanticColors.Text.positiveMid.value
        : neutralColor,
      isEmphasized: false,
      isOverThreshold,
    }
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        // 縦スクロールバーが最終列 (誤差(%)) に被って**数値が読めなくなる**のを防ぐ
        // gutter の予約。 design `css/balance.css:300` の
        // `.region-left .ecl-table-wrap { scrollbar-gutter: stable }` (8080 が
        // 2026-06-22 に同じ理由で入れている)。 呼び出し側が「この幅なら 4 列が
        // 収まる」と明示した時だけ当てる。
        //
        // overflow-x は hidden ではなく auto (#9509 Des 判断)。 hidden だと収まらない
        // 環境で最終列が**切り取られて到達できない** (Windows / Meiryo で「30.(」まで
        // しか見えなかった)。 既定は見出しの折り返しで収め (下記 TH)、 auto は安全網。
        ...(fitsWithoutHorizontalScroll
          ? { overflowX: 'auto' as const, scrollbarGutter: 'stable' }
          : {}),
        border: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        // UI 辞書 components/table.md §0 `.ecl-table-wrap` の角丸 (--radius-lg = 8px。 PlanTable と同じ)
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair --radius-lg)
        borderRadius: '8px',
        background: LightModeSemanticColors.Surface.primary.value,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          // design `css/components.css` の `.ecl-table` は `--font-size-md` = 14px
          // (実装は 12px で 1 段小さかった。 #2941)。 同 repo の [[MasterDataTable]] が
          // 既に `.ecl-table` の実装版なので値をそのまま写す。
          fontSize: fontSizes.md.value,
        }}
      >
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              // `headerHints?.[col.key]` を JSX 内で 2 回書くと TS が narrowing を
              // 引き継がない。 局所 const に落としてから使う。
              const hint = headerHints?.[col.key]
              return (
                <th
                  key={col.key}
                  style={{
                    // design `.ecl-table th` の canonical な見た目は
                    // `TABLE_HEADER_CELL_STYLE` が single source (地 / padding /
                    // weight / 色 / 下罫線 / sticky / whiteSpace: nowrap)。 ここで
                    // 書き写していたため nowrap だけ欠け、 幅が詰まると見出しが
                    // 折り返していた。 textAlign は列ごとに変わるので呼び出し側が持つ。
                    ...TABLE_HEADER_CELL_STYLE,
                    textAlign: col.align,
                    // [design 差異] 4 列を収める画面だけ見出しの折り返しを許す
                    // (#9509 Des 判断)。 spec の列名は単位付き (「計画 (kWh)」
                    // `doji-bg.md` 英語付録) で、 1 行に固定すると見出しが列幅を
                    // 決めて 380px に収まらない (実測: 必要 389px / 使える 363px)。
                    // 単位が 2 行目に回り、 数値列は詰まる。 他の画面は nowrap のまま。
                    ...(fitsWithoutHorizontalScroll
                      ? { whiteSpace: 'normal' as const }
                      : {}),
                    // 最終列 (誤差) は TD と同じ右余白にして列を揃える (上記 TD 参照)。
                    ...(fitsWithoutHorizontalScroll && col.key === 'error'
                      ? { paddingRight: LAST_COLUMN_PADDING_RIGHT }
                      : {}),
                  }}
                >
                  {t(`${labelKeyPrefix}.${col.key}`)}
                  {/* 見出しの補足 (`?` + tooltip)。 渡された列にだけ出す。

                    [design 差異] design (`balance/bg-region.html:158`) は
                    `<th>` 全体を hover 対象 (`data-tooltip` + `cursor:help`) にし、
                    アイコン側は装飾 (tabindex も role も持たない)。 ここでは
                    **アイコンを trigger にする** `HelpIcon` を使う。 design 自身の
                    canonical な `.head-info` パターン (`balance/bg.html:95` ほか) が
                    `tabindex="0" role="img" aria-label` を持つ形で、 そちらが
                    キーボードでも読める (WCAG 2.1 SC 1.4.13)。 `<th>` 全体を
                    trigger にすると、 見出しへ focus する手段が無く hover 専用の
                    情報になる。 */}
                  {hint !== undefined && (
                    <>
                      {' '}
                      <HelpIcon
                        ariaLabel={hint.ariaLabel}
                        content={hint.content}
                      />
                    </>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const isAwaiting =
              row.awaitingVoltageClasses !== undefined &&
              isAwaitingVoltage(row.awaitingVoltageClasses)
            const err = formatError(row, isAwaiting)
            const isMissing = row.actualStatus === 'missing'
            const isInterpolated = row.actualStatus === 'interpolated'
            // `currentKoma` は 0 = 該当なし (過去日・未来日) なので `> 0` で判定する。
            const isCurrent =
              currentKoma !== undefined &&
              currentKoma > 0 &&
              row.koma === currentKoma
            const outline = (
              position: 'first' | 'middle' | 'last',
            ): string | undefined =>
              isCurrent ? currentKomaOutline(position) : undefined
            // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。
            // 線は各セルが持つので、 区切りの行だけセルの下線を濃くする。
            const rowBorderBottom = isKomaDividerAfter(row.koma)
              ? KOMA_DIVIDER_BORDER
              : `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`

            return (
              <tr
                key={row.koma}
                // 現在コマの行 (輪郭と時刻列の太字で示す) を支援技術にも伝える。
                aria-current={isCurrent ? 'true' : undefined}
                style={{
                  background:
                    row.koma % 2 === 0
                      ? LightModeSemanticColors.Surface.secondary.value
                      : undefined,
                }}
              >
                <td
                  style={{
                    padding: '6px 10px',
                    borderBottom: rowBorderBottom,
                    boxShadow: outline('first'),
                    /* design は輪郭と**セットで**時刻列を太字にする
                       (`.koma-table .koma-row-current td:first-child
                       { font-weight: 700 }`)。 輪郭は 1px と細いので、 色と線幅
                       だけに頼らない冗長な手がかりとして効く。 */
                    fontWeight: isCurrent ? 700 : undefined,
                  }}
                >
                  {row.time}〜
                  {/* 印は行に 1 つ、 時刻のセルに置く (design
                      `js/bg-region-data.js` の timeCell)。 実績・誤差のセルは
                      文字色を落とすだけにする。 */}
                  {isAwaiting && <AwaitingVoltageChip />}
                </td>
                <td
                  style={{
                    padding: '6px 10px',
                    textAlign: 'right',
                    borderBottom: rowBorderBottom,
                    fontVariantNumeric: 'tabular-nums',
                    boxShadow: outline('middle'),
                  }}
                >
                  {/* 計画値は需要想定で入力・確定された値なので入力精度のまま出す
                      (docs/specs/README.md §数値表示共通ルールの個別行ルール /
                      AC-BDB-028)。丸めるのは託送実績由来で小数を持つ実績値だけ。 */}
                  {row.plan !== null ? row.plan.toLocaleString('ja-JP') : '—'}
                </td>
                <td
                  /* **× / 斜体が何を意味するかを、 セルの上で読めるようにする。**
                     地の色と字体だけでは「値が届いていない」 のか「値はあるが問題が
                     ある」 のかが区別できない (2026-09-09 デザイナー指摘)。 design
                     `js/bg-region-data.js:117-123` は `title` + `data-tooltip` +
                     `cursor:help` (`bg-region.html:35`) で欠損理由を出しており、
                     実装はここを落としていた。

                     [DS差異] アイコンボタンは DS `Tooltip` で出す規約 (2026-08-17)
                     だが、 ここは**最大 48 行 × 2 列のデータセル**なので portal を
                     行数ぶん作らず、 design と同じ native `title` にする。

                     理由 (「託送実績データ未着」 等) までは出せない。 proto
                     `actual_status` が素の string で reason field を持たないため
                     (別 issue)。 まずは「値が無い」 ことだけを伝える。 */
                  style={{
                    padding: '6px 10px',
                    textAlign: isMissing ? 'center' : 'right',
                    // hover できることを見せる (design `bg-region.html:35`)。
                    ...(isMissing || isInterpolated ? { cursor: 'help' } : {}),
                    borderBottom: rowBorderBottom,
                    fontVariantNumeric: 'tabular-nums',
                    // 欠損 (×) = 値が届いていないコマ。 地は **warning** で、
                    // negative ではない。 design `css/balance.css:375` の
                    // `.actual-missing { background: var(--surface-warning-low) }` +
                    // 同 :376 の mark `var(--text-negative-mid)` がそのまま対応する
                    // (同ファイルのコメント「全損 (negative) より軽い warning」)。
                    //
                    // 実装は生の `rgba(239,68,68,0.06)` (赤の tint) を直書きしており、
                    // **8080 が 2026-07-28 に是正した当の値**がそのまま残っていた。
                    // 文字色は既に negativeMid = 欠損の側なので、地だけが 1 段重い
                    // 全損寄りの見た目になっていた。
                    //
                    // 全損 (実績・補完いずれも取得不可) は design が
                    // `--surface-negative-low` + `--text-negative-high` で**別の状態**
                    // として描き分けているが、実装は `actualStatus` に全損の値を
                    // 持たない (proto `actual_status` は素の string で、frontend が
                    // 許容するのは actual / missing / interpolated / future の 4 値)。
                    // 区別には backend が値を増やす必要があるため issue へ回す。
                    ...(isMissing
                      ? {
                          background:
                            LightModeSemanticColors.Surface.warningLow.value,
                          color: LightModeSemanticColors.Text.negativeMid.value,
                          fontWeight: 'bold',
                        }
                      : {}),
                    // 補完値 (spec doji-bg.md AC-BDB-019 = 補完済みを通常の実績と
                    // 視覚的に区別)。 欠損 / 全損は「値が無い」深刻度の軸だが、
                    // 補完は「値はあるが推定」で軸が違ううえ深刻度は最も低いため、
                    // 有彩色は使わず中立の tertiary を敷く。 識別は italic +
                    // mid-emphasis が主で地は補助 (design css/balance.css L378、
                    // 2026-07-28 に同じ rgba 直書きをこの値へ是正済み)。
                    ...(isInterpolated
                      ? {
                          color: LightModeSemanticColors.Text.midEmphasis.value,
                          fontStyle: 'italic',
                          background:
                            LightModeSemanticColors.Surface.tertiary.value,
                        }
                      : {}),
                    // 低圧未着 (AC-BDB-041) は低圧ぶんが欠けた値なので文字色を落とす。
                    // 欠損・補完と重なった時はそれぞれの見た目を優先する (design
                    // `css/balance.css` の `td.lv-pending`)。
                    ...(isAwaiting && !isMissing && !isInterpolated
                      ? {
                          color: LightModeSemanticColors.Text.midEmphasis.value,
                        }
                      : {}),
                    boxShadow: outline('middle'),
                  }}
                  title={
                    isMissing
                      ? t(`${labelKeyPrefix}.missingHint`)
                      : isInterpolated
                        ? t(`${labelKeyPrefix}.interpolatedHint`)
                        : undefined
                  }
                >
                  {isMissing
                    ? t(`${labelKeyPrefix}.missing`)
                    : row.actual !== null
                      ? formatKwh(row.actual)
                      : '—'}
                </td>
                <td
                  data-over-threshold={
                    err?.isOverThreshold === true || undefined
                  }
                  style={{
                    padding: '6px 10px',
                    textAlign: 'right',
                    borderBottom: rowBorderBottom,
                    fontVariantNumeric: 'tabular-nums',
                    ...(fitsWithoutHorizontalScroll
                      ? { paddingRight: LAST_COLUMN_PADDING_RIGHT }
                      : {}),
                    color: err?.color,
                    // design css/balance.css L306 `.err-minus { font-weight:600 }`。
                    // 閾値を超えたマイナス乖離だけ太らせる (旧実装は ▲ が付く行を
                    // 全て bold にしていた)。
                    fontWeight: err?.isEmphasized === true ? 600 : undefined,
                    boxShadow: outline('last'),
                  }}
                >
                  {err?.text ?? '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
